import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, FileDown, Loader2, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, mediaUrl, domaineLabel, fmtDate, STATUTS, URGENCES } from "@/lib/api";
import { StatutBadge } from "@/pages/Suivi";
import SignaturePad from "@/components/SignaturePad";

const Avancement = ({ statut }) => {
  const currentIdx = STATUTS.findIndex((s) => s.key === statut);
  return (
    <div className="rounded-2xl border border-slate-200 p-5">
      <div className="bk-label mb-4">Avancement</div>
      <div className="space-y-3">
        {STATUTS.map((s, i) => (
          <div key={s.key} className="flex items-center gap-3">
            <span className={`h-6 w-6 rounded-full grid place-items-center text-[10px] font-bold ${i <= currentIdx ? "bg-[#005CFF] text-white" : "bg-slate-100 text-slate-400"}`}>
              {i <= currentIdx ? <Check size={12} /> : i + 1}
            </span>
            <span className={`text-sm ${i <= currentIdx ? "font-semibold text-slate-900" : "text-slate-400"}`}>{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Historique = ({ entrees }) => (
  <div className="rounded-2xl border border-slate-200 p-5">
    <div className="bk-label mb-3">Historique</div>
    <div className="space-y-3">
      {entrees?.map((h, i) => (
        <div key={`${h.date}-${i}`} className="text-sm">
          <div className="font-semibold text-slate-900">{h.statut}</div>
          <div className="text-slate-500 text-xs">
            {fmtDate(h.date)} {h.commentaire ? `· ${h.commentaire}` : ""}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const PanneauCloture = ({ signature, setSignature, csat, setCsat, commentaire, setCommentaire, busy, cloturer }) => (
  <div className="rounded-2xl border border-slate-900 p-5 space-y-5" data-testid="cloture-panel">
    <div>
      <div className="bk-label mb-1">Validation d'intervention</div>
      <p className="text-sm text-slate-600">
        Notez votre satisfaction et, si vous le souhaitez, signez du doigt sur l'écran. La signature est facultative : elle est simplement
        intégrée au rapport PDF quand elle est apposée.
      </p>
    </div>
    <SignaturePad onChange={setSignature} />
    <div>
      <div className="bk-label mb-2">Satisfaction</div>
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} data-testid={`csat-${n}`} onClick={() => setCsat(n)} className="p-1">
            <Star size={26} className={n <= csat ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
          </button>
        ))}
      </div>
    </div>
    <Textarea data-testid="csat-commentaire" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} rows={3} placeholder="Un commentaire (optionnel)" />
    <Button data-testid="cloture-submit-btn" onClick={cloturer} disabled={busy} className="w-full h-13 py-3 rounded-xl bg-[#005CFF] hover:bg-[#0047CC]">
      {busy ? <Loader2 className="animate-spin" size={18} /> : signature ? "Valider et signer la clôture" : "Valider sans signature"}
    </Button>
  </div>
);

export const BoutonRapport = ({ ticketId, reference, testid = "rapport-pdf-btn" }) => {
  const [busy, setBusy] = useState(false);

  const telecharger = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/tickets/${ticketId}/rapport.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rapport-${reference}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 20000);
    } catch (e) {
      toast.error(errMsg(e, "Rapport indisponible"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button data-testid={testid} onClick={telecharger} disabled={busy} variant="outline" className="rounded-xl">
      <FileDown size={15} className="mr-2" /> {busy ? "Génération…" : "Rapport PDF"}
    </Button>
  );
};

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [signature, setSignature] = useState("");
  const [csat, setCsat] = useState(0);
  const [commentaire, setCommentaire] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/tickets/${id}`).then(({ data }) => setTicket(data));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const cloturer = async () => {
    if (!csat) return toast.error("Notez votre satisfaction");
    setBusy(true);
    try {
      await api.post(`/tickets/${id}/cloture`, { signature, csat, commentaire });
      toast.success(signature ? "Clôture signée et enregistrée. Merci !" : "Clôture enregistrée. Merci !");
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Erreur"));
    } finally {
      setBusy(false);
    }
  };

  const supprimer = async () => {
    if (!window.confirm(`Supprimer définitivement le ticket ${ticket.reference} ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/tickets/${id}`);
      toast.success("Ticket supprimé");
      navigate("/suivi");
    } catch (e) {
      toast.error(errMsg(e, "Suppression impossible"));
    }
  };

  if (!ticket) return <div className="text-slate-400 text-sm">Chargement…</div>;

  const urgence = URGENCES.find((u) => u.key === ticket.urgence);
  const showCloture = ticket.statut === "resolu" && !ticket.cloture_validee && !ticket.signature_client && !ticket.resolu_automatiquement;

  return (
    <div className="space-y-8" data-testid="ticket-detail">
      <div className="flex items-center gap-3">
        <button data-testid="detail-back-btn" onClick={() => navigate("/suivi")} className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:text-slate-900">
          <ArrowLeft size={16} />
        </button>
        <div>
          <div className="font-mono-bk text-sm text-slate-500">{ticket.reference}</div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">{domaineLabel(ticket.domaine)}</h1>
          {ticket.materiel_concerne && (
            <div className="text-xs text-slate-500 mt-1" data-testid="ticket-materiel">
              {ticket.materiel_concerne}
              {ticket.materiel_serie ? ` · SN ${ticket.materiel_serie}` : ""}
              {ticket.materiel_date_pose ? ` · posé le ${ticket.materiel_date_pose}` : ""}
            </div>
          )}
        </div>
        <button
          data-testid="ticket-delete-btn"
          onClick={supprimer}
          title="Supprimer ce ticket"
          className="ml-auto p-2 rounded-lg border border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-200 transition-colors"
        >
          <Trash2 size={16} />
        </button>
      </div>

      <BoutonRapport ticketId={ticket.id} reference={ticket.reference} testid="rapport-pdf-header-btn" />

      <div className="flex flex-wrap items-center gap-2">
        <StatutBadge statut={ticket.statut} />
        <span className="text-xs font-semibold px-2.5 py-1 rounded-full border" style={{ borderColor: urgence?.color, color: urgence?.color }}>
          {urgence?.label} · {urgence?.sla}
        </span>
        <span className={`text-xs px-2.5 py-1 rounded-full border ${ticket.contrat?.actif ? "border-emerald-200 text-emerald-700 bg-emerald-50" : "border-amber-200 text-amber-700 bg-amber-50"}`}>
          {ticket.contrat?.actif ? "Inclus au contrat" : "Hors contrat"}
        </span>
        {ticket.statut !== "resolu" && ticket.prise_en_charge_label && (
          <span className="text-xs px-2.5 py-1 rounded-full border border-blue-200 text-blue-700 bg-blue-50" data-testid="detail-prise-en-charge">
            Prise en charge {ticket.prise_en_charge_label}
          </span>
        )}
      </div>

      <Avancement statut={ticket.statut} />

      {ticket.qcm_path?.length > 0 && (
        <div className="rounded-2xl border border-slate-200 p-5" data-testid="detail-qcm">
          <div className="bk-label mb-3">Parcours de qualification</div>
          <div className="space-y-2">
            {ticket.qcm_path.map((s, i) => (
              <div key={`${s.question}-${i}`} className="text-sm">
                <span className="text-slate-500">{s.question} → </span>
                <span className="font-semibold text-slate-900">{s.reponse}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(ticket.description_texte || ticket.transcription_vocale) && (
        <div className="rounded-2xl border border-slate-200 p-5 space-y-4">
          {ticket.description_texte && (
            <div>
              <div className="bk-label mb-2">Description</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{ticket.description_texte}</p>
            </div>
          )}
          {ticket.transcription_vocale && (
            <div>
              <div className="bk-label mb-2">Message vocal transcrit</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{ticket.transcription_vocale}</p>
            </div>
          )}
        </div>
      )}

      {ticket.photos?.length > 0 && (
        <div>
          <div className="bk-label mb-3">Photos</div>
          <div className="grid grid-cols-3 gap-3">
            {ticket.photos.map((p, i) => (
              <img key={`photo-${i}`} src={p} alt={`photo ${i + 1}`} className="aspect-square object-cover rounded-xl border border-slate-200" />
            ))}
          </div>
        </div>
      )}

      {ticket.photos_resolution?.length > 0 && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-5" data-testid="detail-photos-resolution">
          <div className="bk-label mb-1 text-emerald-800">Après intervention</div>
          <p className="text-xs text-slate-600 mb-3">Photos prises par notre technicien à l'issue de l'intervention.</p>
          <div className="grid grid-cols-3 gap-3">
            {ticket.photos_resolution.map((p) => (
              <a key={p.url} href={mediaUrl(p.url)} target="_blank" rel="noreferrer" data-testid={`detail-resolution-${p.url.split("/").pop()}`}>
                <img src={mediaUrl(p.url)} alt="Après intervention" className="aspect-square object-cover rounded-xl border border-emerald-200" />
              </a>
            ))}
          </div>
          {ticket.photos_resolution.find((p) => p.commentaire) && (
            <p className="text-sm text-slate-700 mt-3">{ticket.photos_resolution.filter((p) => p.commentaire).map((p) => p.commentaire).join(" · ")}</p>
          )}
        </div>
      )}

      {ticket.video_url && (
        <div className="rounded-2xl border border-slate-200 p-5" data-testid="detail-video">
          <div className="bk-label mb-3">Votre vidéo</div>
          <video src={mediaUrl(ticket.video_url)} controls playsInline className="w-full rounded-xl border border-slate-200" />
        </div>
      )}

      {ticket.messages?.length > 0 && (
        <div className="rounded-2xl border border-[#005CFF]/30 bg-blue-50/50 p-5" data-testid="detail-messages">
          <div className="bk-label mb-3 text-[#0047CC]">Réponses de BKPRO</div>
          <div className="space-y-4">
            {ticket.messages.map((m) => (
              <div key={`${m.date}-${m.auteur}`} data-testid={`detail-message-${m.date}`}>
                <p className="text-sm text-slate-800 whitespace-pre-wrap">{m.message}</p>
                <p className="text-xs text-slate-500 mt-1">
                  {m.auteur} · {fmtDate(m.date)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      <Historique entrees={ticket.historique} />

      {showCloture && (
        <PanneauCloture
          signature={signature}
          setSignature={setSignature}
          csat={csat}
          setCsat={setCsat}
          commentaire={commentaire}
          setCommentaire={setCommentaire}
          busy={busy}
          cloturer={cloturer}
        />
      )}

      {(ticket.cloture_validee || ticket.signature_client) && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5" data-testid="cloture-done">
          <div className="bk-label mb-2">Intervention validée</div>
          {ticket.signature_client ? (
            <img src={ticket.signature_client} alt="signature" className="h-20 bg-white rounded-lg border border-slate-200" />
          ) : (
            <p className="text-sm text-slate-600">Validée sans signature électronique.</p>
          )}
          {ticket.csat ? <div className="text-sm text-slate-700 mt-3">Satisfaction : {ticket.csat}/5</div> : null}
          <div className="mt-4">
            <BoutonRapport ticketId={ticket.id} reference={ticket.reference} />
          </div>
        </div>
      )}
    </div>
  );
}
